import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools } from 'zustand/middleware';
import { 
  DrawingElement, 
  ViewTransform, 
  GridSettings, 
  SnapSettings
} from '../types/nnstructural2D';
import { ViewMode, ViewSettings, WorkspaceSettings } from '../types/workspace.types';

interface DrawingState {
  // Drawing Elements
  elements: DrawingElement[];
  selectedElementIds: string[];
  selectedTool: 'select' | 'beam' | 'column' | 'node' | 'dimension' | 'pan' | 'zoom';
  isDrawing: boolean;
  previewElement: DrawingElement | null;
  
  // View Settings
  viewTransform: ViewTransform;
  canvasSize: { width: number; height: number };
  viewMode: ViewMode;
  
  // Grid Settings
  gridSize: number;
  snapToGrid: boolean;
  showAxes: boolean;
  showGrid: boolean;
  gridSettings: GridSettings;
  snapSettings: SnapSettings;
  
  // Workspace Settings
  settings: WorkspaceSettings;
}

type DrawingStore = DrawingState & {
  // Element Actions
  addElement: (element: DrawingElement) => void;
  updateElement: (id: string, updates: Partial<DrawingElement>) => void;
  removeElement: (id: string) => void;
  removeElements: (ids: string[]) => void;
  clearElements: () => void;
  
  // Selection Actions
  setSelectedElements: (elementIds: string[]) => void;
  addToSelection: (elementId: string) => void;
  removeFromSelection: (elementId: string) => void;
  clearSelection: () => void;
  
  // View Actions
  setViewMode: (mode: ViewMode) => void;
  // Unified Zoom API
  setZoom: (zoom: number) => void; // legacy; delegates to setZoomUnified with origin 'center'
  setZoomUnified: (zoom: number, meta?: { origin?: 'center' | 'cursor'; clientX?: number; clientY?: number }) => void;
  // Expose last zoom request for consumers (e.g., CameraController)
  lastZoomRequest?: { level: number; origin: 'center' | 'cursor'; clientX?: number; clientY?: number; id: number } | null;
  setPan: (pan: { x: number; y: number }) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomToFit: () => void;
  resetView: () => void;
  
  // Grid Actions
  setGridSize: (size: number) => void;
  toggleSnapToGrid: () => void;
  toggleAxes: () => void;
  toggleGrid: () => void;
  setGridVisibility: (visible: boolean) => void;
  setGridSettings: (settings: Partial<GridSettings>) => void;
  setViewSettings: (settings: Partial<ViewSettings>) => void;
};

const initialState: DrawingState = {
  elements: [],
  selectedElementIds: [],
  selectedTool: 'select',
  isDrawing: false,
  previewElement: null,
  viewTransform: {
    scale: 1.0,
    translateX: 0,
    translateY: 0,
    rotation: 0
  },
  canvasSize: { width: 800, height: 600 },
  viewMode: 'FRONT',
  gridSize: 10,
  snapToGrid: true,
  showAxes: true,
  showGrid: false,
  gridSettings: {
    spacing: 1.0,
    majorSpacing: 5.0,
    color: '#e0e0e0',
    majorColor: '#c0c0c0',
    visible: false,
    snapToGrid: true,
    units: 'm',
    scale: 1.0
  },
  snapSettings: {
    enabled: true,
    tolerance: 10,
    snapToGrid: true,
    snapToEndpoints: true,
    snapToMidpoints: true,
    snapToIntersections: true,
    snapToPerpendicular: true,
    snapToParallel: true,
    showSnapIndicator: true
  },
  settings: {
    grid: {
      visible: false,
      spacing: 10,
      size: 100,
      color: '#e0e0e0',
      subColor: '#f0f0f0'
    },
    view: {
      mode: 'FRONT',
      camera: {
        // Front view: looking along -Z axis towards origin
        position: { x: 0, y: 0, z: 30 },
        target: { x: 0, y: 0, z: 0 },
        up: { x: 0, y: 1, z: 0 }
      }
    }
  }
};

export const useDrawingStore = create<DrawingStore>()(
  devtools(
    immer((set) => ({
      ...initialState,

      // Element Actions
      addElement: (element) => set((state) => { state.elements.push(element); }),
      updateElement: (id, updates) => set((state) => {
        const index = state.elements.findIndex((e: DrawingElement) => e.id === id);
        if (index !== -1) {
          state.elements[index] = { ...state.elements[index], ...updates };
        }
      }),
      removeElement: (id) => set((state) => {
        state.elements = state.elements.filter((e: DrawingElement) => e.id !== id);
      }),
      removeElements: (ids) => set((state) => {
        state.elements = state.elements.filter((e: DrawingElement) => !ids.includes(e.id));
      }),
      clearElements: () => set((state) => { state.elements = []; }),

      // Selection Actions
      setSelectedElements: (elementIds) => set({ selectedElementIds: elementIds }),
      addToSelection: (elementId) => set((state) => {
        if (!state.selectedElementIds.includes(elementId)) {
          state.selectedElementIds.push(elementId);
        }
      }),
      removeFromSelection: (elementId) => set((state) => {
        state.selectedElementIds = state.selectedElementIds.filter(id => id !== elementId);
      }),
      clearSelection: () => set({ selectedElementIds: [] }),

      // View Actions
      setViewMode: (mode) => set({ viewMode: mode }),
      setZoom: (zoom) => {
        // maintain backward compatibility; treat as center-origin zoom
        (useDrawingStore.getState().setZoomUnified)(zoom, { origin: 'center' });
      },
      setZoomUnified: (zoom, meta) => set((state) => {
        // clamp 10%..400%
        const clamped = Math.max(0.1, Math.min(4.0, zoom));
        state.viewTransform.scale = clamped;
        const reqId = Date.now();
        state.lastZoomRequest = {
          level: clamped,
          origin: meta?.origin ?? 'center',
          clientX: meta?.clientX,
          clientY: meta?.clientY,
          id: reqId
        };
      }),
      setPan: (pan) => set((state) => {
        state.viewTransform.translateX = pan.x;
        state.viewTransform.translateY = pan.y;
      }),
      zoomIn: () => {
        const s = useDrawingStore.getState();
        s.setZoomUnified(s.viewTransform.scale * 1.1, { origin: 'center' });
      },
      zoomOut: () => {
        const s = useDrawingStore.getState();
        s.setZoomUnified(s.viewTransform.scale / 1.1, { origin: 'center' });
      },
      zoomToFit: () => set(() => {
        // Implementation
      }),
      resetView: () => set((state) => {
        state.viewTransform = initialState.viewTransform;
      }),

      // Grid Actions
      setGridSize: (size) => set({ gridSize: size }),
      toggleSnapToGrid: () => set((state) => ({ snapToGrid: !state.snapToGrid })),
      toggleAxes: () => set((state) => ({ showAxes: !state.showAxes })),
      toggleGrid: () => set((state) => ({ showGrid: !state.showGrid })),
      setGridVisibility: (visible) => set({ showGrid: visible }),
      setGridSettings: (settings) => set((state) => {
        state.settings.grid = { ...state.settings.grid, ...settings };
      }),
      setViewSettings: (settings) => set((state) => {
        state.settings.view = { ...state.settings.view, ...settings };
      })
    }))
  )
); 